// A code-split route that can be PRELOADED for real (2026-09-11, user
// report: opening the Lab from a home chip showed "a rerender glitch once"
// — on a phone, a full frame of the board skeleton before the page).
//
// `React.lazy` cannot do this. Its loader is only called on the first
// RENDER, and in React 18 a lazy component suspends on that render even
// when its promise is already resolved — the resolution is read in a
// `.then`, a microtask later. The home warmed the chunk with a bare
// `import()`, which filled the module cache but not the lazy's own state,
// so the first render still suspended. Inside the view transition's
// synchronous commit (`flushSync`, utils/viewTransition) a suspended route
// commits its Suspense fallback at once: the transition snapshotted the
// board skeleton, and the Lab page popped in a frame later with no
// transition at all.
//
// This wrapper keeps the loaded module in a variable: once `preload()` has
// resolved, the route renders synchronously on its first render, so the
// transition snapshots the real page. Not yet loaded (a cold link straight
// to the URL), it suspends with the same promise, and the router's
// Suspense fallback shows exactly as with `lazy` — same behaviour, one
// synchronous fast path. Call `preload()` wherever the reader is likely
// to go next (the demo home, for the Lab).
//
// A load can FAIL (2026-10-01, when the wall began warming the home: a
// reader on a bad connection). A failed WARM call is forgotten — the next
// call, or the route's own render, simply tries again. A failed load the
// ROUTE itself was waiting on is thrown from its next render, as `lazy`
// does, so the error boundary speaks; re-throwing the rejected promise
// instead would have React retry the render against it for ever.
import { createElement } from "react";

export function preloadable(loader) {
  let mod = null;
  let promise = null;
  let failure = null;
  const preload = () => {
    if (!promise) {
      promise = loader().then(
        (m) => {
          mod = m;
          return m;
        },
        (err) => {
          promise = null;
          throw err;
        },
      );
    }
    return promise;
  };
  function Preloadable(props) {
    if (mod) return createElement(mod.default, props);
    if (failure) throw failure;
    throw preload().catch((err) => {
      failure = err;
    });
  }
  return { Component: Preloadable, preload };
}
